/**
 * Routing helpers for task notifications.
 *
 * The notification body may arrive as a plain string (legacy / simple kinds)
 * or as a JSON-encoded object (cross-org DRAFT_* kinds denormalize routing
 * hints into the body — `target_head_office_id`, `task_id`, `task_title`).
 *
 * `destinationFor` decides where a notification click should navigate:
 *   - DRAFT_RECEIVED / DRAFT_RESUBMITTED → admin's Draft Tasks tab
 *   - DRAFT_SUBMITTED / DRAFT_APPROVED   → originator's Partner Task page
 *   - DRAFT_SEND_BACK / DRAFT_REJECTED   → originator's Partner Task page,
 *     deep-linked to the Activity tab so the reason is immediately visible
 *   - CLIENT_TASK_CREATED / CLIENT_TASK_STATUS → /partner-task/{task_id}
 *     (recipient is a linked partner user; no portal page in messenger)
 *   - everything else → /tasks/{task_id} (existing behaviour)
 */

export function parseNotificationBody(
  body: string | null | undefined,
): Record<string, any> {
  if (!body) return {};
  if (typeof body !== "string") return body as any;
  try {
    const parsed = JSON.parse(body);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

export function destinationFor(
  kind: string,
  taskId: string,
  _bodyStr?: string | null,
): string {
  if (taskId && taskId.startsWith("leave:")) return "/employee/leaves";
  if (taskId && taskId.startsWith("expense:")) return "/employee/expenses";
  if (taskId && taskId.startsWith("salary:")) return "/employee/payroll";
  if (taskId && taskId.startsWith("asset:")) return "/employee/assets";
  // Attendance (late-coming warning) notifications ride the bell with a
  // namespaced task_id of "attendance:<date>" — route them to the attendance
  // page so the employee lands on the punches the warning is about.
  if (taskId && taskId.startsWith("attendance:")) return "/employee/attendance";

  switch (kind) {
    case "DRAFT_RECEIVED":
    case "DRAFT_RESUBMITTED":
      return `/tasks/list?scope=drafts&review=${encodeURIComponent(taskId)}`;
    case "DRAFT_SUBMITTED":
    case "DRAFT_APPROVED":
      return `/partner-task/${encodeURIComponent(taskId)}`;
    case "DRAFT_SEND_BACK":
    case "DRAFT_REJECTED":
      return `/partner-task/${encodeURIComponent(taskId)}?tab=activity`;
    // Client/partner task kinds — emitted to ClientDetails.linked_user_id
    // recipients, who can't view the standard /tasks/{id} page. Route them to
    // the partner task detail page.
    case "CLIENT_TASK_CREATED":
    case "CLIENT_TASK_STATUS":
      return `/partner-task/${encodeURIComponent(taskId)}`;
    default:
      return `/tasks/${taskId}`;
  }
}
